import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  searchAuditQuestionsApiThunk,
  updateQuestionResponseApiThunk,
  getAllQuestionResponsesApiThunk,
  getQuestionResponseByIdApiThunk,
  resetApiStatus,
  setCurrentQuestionIndex,
  setFilteredQuestionIds,
  updateQuestionResponseState,
} from './questionResponsesSlice';
import { RootState } from '..';
import {
  UpdateAuditQuestionResponseRequest,
  ListAuditQuestionResponsesRequest,
  GetAuditQuestionResponseRequest,
  AuditQuestionItemResponse,
} from 'src/types/helios/QuestionResponse';
import { SearchAuditQuestionsRequest } from 'src/types/helios/SearchAuditQuestions';

export const useQuestionResponses = () => {
  const dispatch = useAppDispatch();

  const auditQuestionsData = useAppSelector(
    (state: RootState) => state.questionResponses.auditQuestionsData,
  );

  const auditQuestionResponses = useAppSelector(
    (state: RootState) => state.questionResponses.auditQuestionResponses,
  );
  const searchQuestionsApiStatus = useAppSelector(
    (state: RootState) => state.questionResponses.searchQuestionsApiStatus,
  );
  const updateQuestionResponseApiStatus = useAppSelector(
    (state: RootState) => state.questionResponses.updateQuestionResponseApiStatus,
  );
  const updateResponseErrorCode = useAppSelector(
    (state: RootState) => state.questionResponses.updateResponseErrorCode,
  );
  const getAllApiStatus = useAppSelector(
    (state: RootState) => state.questionResponses.getAllApiStatus,
  );
  const getByIdApiStatus = useAppSelector(
    (state: RootState) => state.questionResponses.getByIdApiStatus,
  );
  const currentQuestionIndex = useAppSelector(
    (state: RootState) => state.questionResponses.currentQuestionIndex,
  );

  const filteredQuestionIds = useAppSelector(
    (state: RootState) => state.questionResponses.filteredQuestionIds,
  );

  const searchAuditQuestions = useCallback(
    (data: SearchAuditQuestionsRequest) => {
      dispatch(searchAuditQuestionsApiThunk(data));
    },
    [dispatch],
  );

  const updateQuestionResponse = useCallback(
    (data: UpdateAuditQuestionResponseRequest) => {
      dispatch(updateQuestionResponseApiThunk(data));
    },
    [dispatch],
  );

  const getAllQuestionResponses = useCallback(
    (data: ListAuditQuestionResponsesRequest) => {
      dispatch(getAllQuestionResponsesApiThunk(data));
    },
    [dispatch],
  );

  const getQuestionResponseById = useCallback(
    (data: GetAuditQuestionResponseRequest) => {
      dispatch(getQuestionResponseByIdApiThunk(data));
    },
    [dispatch],
  );

  const resetStatus = useCallback(() => {
    dispatch(resetApiStatus());
  }, [dispatch]);

  const setCurrentQuestion = useCallback(
    (index: number) => {
      dispatch(setCurrentQuestionIndex(index));
    },
    [dispatch],
  );

  const setFilteredQuestions = useCallback(
    (questionIds: string[] | null) => {
      dispatch(setFilteredQuestionIds(questionIds));
    },
    [dispatch],
  );

  const updateQuestionResponseList = useCallback(
    (data: AuditQuestionItemResponse) => {
      dispatch(updateQuestionResponseState(data));
    },
    [dispatch],
  );

  return {
    auditQuestionsData,
    searchQuestionsApiStatus,
    updateQuestionResponseApiStatus,
    updateResponseErrorCode,
    getAllApiStatus,
    getByIdApiStatus,
    auditQuestionResponses,
    currentQuestionIndex,
    filteredQuestionIds,
    searchAuditQuestions,
    updateQuestionResponse,
    getAllQuestionResponses,
    getQuestionResponseById,
    resetStatus,
    setCurrentQuestion,
    setFilteredQuestions,
    updateQuestionResponseList,
  };
};
